import * as vp from 'vopay';

export type PropagateRoleResult = {
  numTotal: number;
  numCreated: number;
  numFailed: number;
};

type Deps = {
  fetchSubAccountsAsync: (params: vp.SubAccountsRequest) => Promise<vp.SubAccountsResponse>;
  createUserRole: (
    params: vp.CreateAccountUserRoleRequest,
    bearer?: string,
  ) => Promise<vp.CreateAccountUserRoleResponse>;
};

/**
 * Fan-out helper: creates the same role in every active sub-account using the
 * sub-account's bearer token. Mirrors `propagateToSubAccounts` for workflows.
 *
 * Each sub-account is hit in parallel (Promise.all). Failures inside a single
 * sub-account don't abort the batch — we return a summary so the caller can
 * surface a partial-success message. A failure of the outer sub-accounts
 * fetch, however, throws so the caller doesn't mistake it for "zero
 * sub-accounts".
 */
export async function propagateRoleToSubAccounts(
  role: vp.CreateAccountUserRoleRequest,
  { fetchSubAccountsAsync, createUserRole }: Deps,
): Promise<PropagateRoleResult> {
  const subAccountsRes = await fetchSubAccountsAsync({ ShowToken: true, ActiveOnly: true });
  if (!subAccountsRes.Success) {
    throw new Error(subAccountsRes.ErrorMessage || 'Failed to fetch sub-accounts');
  }

  const subAccounts = subAccountsRes.Subaccounts;

  const results = await Promise.all(
    subAccounts.map(async (subAccount) => {
      try {
        await createUserRole(role, subAccount.Token);
        return { ok: true };
      } catch (error) {
        console.error(
          `Failed to create role "${role.Name}" in sub-account ${subAccount.AccountName}:`,
          error,
        );
        return { ok: false };
      }
    }),
  );

  return {
    numTotal: subAccounts.length,
    numCreated: results.filter((r) => r.ok).length,
    numFailed: results.filter((r) => !r.ok).length,
  };
}
